import type { AccessOrganization, AccessUser, UserRole } from '../../../types/access';
import type { AdminOrganizationDto, AdminUserDto, UserRoleDto } from './dto';

/**
 * Единственное место, где имена полей бэка встречаются в коде.
 * Компоненты и хуки знают только типы из types/access.
 */

/** id везде приводим к строке: Mongo отдаёт ObjectId, а не число. */
const toId = (value: string | number): string => String(value);

const ROLE_FROM_DTO: Record<string, UserRole> = {
    super_admin: 'superadmin',
    superadmin: 'superadmin',
    admin: 'admin',
    user: 'user',
};

/** Обратная карта — для отправки на бэк. */
export const ROLE_TO_DTO: Record<UserRole, UserRoleDto> = {
    superadmin: 'super_admin',
    admin: 'admin',
    user: 'user',
};

/** Незнакомую роль показываем как обычного пользователя, а не падаем. */
const toRole = (value: string): UserRole => ROLE_FROM_DTO[value] ?? 'user';

export function toAccessUser(dto: AdminUserDto): AccessUser {
    const organizationName = dto.organization?.name ?? dto.org ?? null;
    const organizationId = dto.organization?.id ?? dto.organization_id ?? null;

    // Бэк может отдавать признак блокировки любым из двух полей.
    // Если не отдаёт ни одного — считаем учётку активной.
    const blocked = dto.is_blocked === true || dto.is_active === false;

    return {
        id: toId(dto.id),
        fullName: dto.full_name?.trim() || dto.email,
        email: dto.email,
        organization: organizationName,
        organizationId: organizationId === null ? null : toId(organizationId),
        role: toRole(dto.role),
        status: blocked ? 'blocked' : 'active',
        lastLoginAt: dto.last_login_at ?? dto.last_login ?? null,
        maxScanProductDay: dto.max_scan_product_day ?? null,
        maxScanProductMonth: dto.max_scan_product_month ?? null,
    };
}

export function toAccessOrganization(dto: AdminOrganizationDto): AccessOrganization {
    const users = (dto.users ?? []).map((user) => ({
        id: toId(user.id),
        fullName: user.full_name?.trim() || user.email,
        email: user.email,
    }));

    return {
        id: toId(dto.id),
        name: dto.name?.trim() || 'Без названия',
        inn: dto.inn ?? null,
        usersCount: users.length,
        users,
        requestsUsed: dto.count_scan_in_month ?? 0,
        requestsLimit: dto.scan_limit_max ?? 0,
        type: dto.type ?? null,
        address: dto.address ?? null,
        postalCode: dto.postal_code ?? null,
    };
}